import { Controller, useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal"
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { EntregaObraClienteOcorrenciaForm, EntregaObraClienteOcorrenciaSchema } from "../../../schemas/entrega_obra_cliente_verificacao/entrega_obra_cliente_ocorrencia";
import { VerificacaoChecklistModel } from "../../../../domain/models/verificacao_checklist";
import { InformacoesEntregaObraClienteModel } from "../../../../domain/models/informacoes_entrega_obra_cliente";
import { IconButton, styled } from "@mui/material";
import { normalizeFileName } from "../../../../../../common/utils/utils";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * OcorrenciaModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  inspecoesObraItens: VerificacaoChecklistModel[];
  selectedInspecaoObraItem: VerificacaoChecklistModel | null;
  selectedOcorrencia: InformacoesEntregaObraClienteModel | null;

  onDownload: (idInformacoesEntregaObraCliente: number, nroArquivo: number) => void,
  onDeleteFile: (nroArquivo: number) => void;
  onSubmit: (form: EntregaObraClienteOcorrenciaForm) => void;
  onClose: () => void;
};

export const OcorrenciaModal = ({
  open,
  saving,
  loading,
  inspecoesObraItens,
  selectedInspecaoObraItem,
  selectedOcorrencia,
  onDownload,
  onDeleteFile,
  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<EntregaObraClienteOcorrenciaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(EntregaObraClienteOcorrenciaSchema),
  });

  const inspecaoOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: '1ª Inspeção'
      },
      {
        id: 2,
        label: '2ª Inspeção'
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      if (selectedOcorrencia) {
        hookForm.setValue('idVerificacaoChecklist', selectedOcorrencia.idVerificacaoChecklist ?? null);
        hookForm.setValue('descricao', selectedOcorrencia.descricao ?? '');
        hookForm.setValue('dataDescricao', selectedOcorrencia.dataDescricao ?? null);
        hookForm.setValue('dataTratativa', selectedOcorrencia.dataTratativa ?? null);
        hookForm.setValue('tratativa', selectedOcorrencia.tratativa ?? '');
        hookForm.setValue('inspecao', selectedOcorrencia.inspecao === "1" ? 1 : 2);
        hookForm.setValue('descricaoArquivo', selectedOcorrencia.descricaoArquivo ?? "");
        hookForm.setValue('descricaoArquivo2', selectedOcorrencia.descricaoArquivo2 ?? "");
      } else {
        hookForm.reset({
          idVerificacaoChecklist: selectedInspecaoObraItem && selectedInspecaoObraItem.idVerificacaoCliente
            ? selectedInspecaoObraItem.idVerificacaoCliente
            : null,
          inspecao: 1,
          dataDescricao: new Date(),
          arquivo: null,
          arquivo2: null,
          limparArquivo: false,
          limparArquivo2: false,
          nomeArquivo: null,
          nomeArquivo2: null,

        });
      }
    } else {
      hookForm.reset({
        idVerificacaoChecklist: null,
        inspecao: 1,
        dataDescricao: null,
        arquivo: null,
        arquivo2: null,
        limparArquivo: false,
        limparArquivo2: false,
        nomeArquivo: null,
        nomeArquivo2: null,
      });
    }
  }, [
    open,
    selectedOcorrencia,
    selectedInspecaoObraItem,
    hookForm,
  ]);


  //arquivos
  const handleDownload = useCallback((nroArquivo: number) => {
    if (!saving && selectedOcorrencia)
      if (nroArquivo === 1)
        onDownload(selectedOcorrencia.idInformacoesEntregaObraCliente ?? 0, 1);
      else if (nroArquivo === 2)
        onDownload(selectedOcorrencia.idInformacoesEntregaObraCliente ?? 0, 2);
  }, [
    saving,
    selectedOcorrencia,
    onDownload,
  ]);

  const handleDeleteFile = useCallback((nroArquivo: number) => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        if (nroArquivo === 1) {
          hookForm.setValue('limparArquivo', true);
          hookForm.setValue('arquivo', null);
          hookForm.setValue('nomeArquivo', null);
        } else if (nroArquivo === 2) {
          hookForm.setValue('limparArquivo2', true);
          hookForm.setValue('arquivo2', null);
          hookForm.setValue('nomeArquivo2', null);
        }
        onDeleteFile(nroArquivo);
      }
    }
  }, [
    saving,
    hookForm,
    onDeleteFile,
  ]);


  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void, nroArquivo: number) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      if (nroArquivo === 1) {
        hookForm.setValue('limparArquivo', false);
        hookForm.setValue('arquivo', null);
        hookForm.setValue('limparArquivo', false);
      } else if (nroArquivo === 2) {
        hookForm.setValue('limparArquivo2', false);
        hookForm.setValue('arquivo2', null);
        hookForm.setValue('limparArquivo2', false);
      }
      onChange(e.target.files.item(0));
    }
  }, [
    saving,
    hookForm,
  ]);



  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title='Ocorrência'
      width="60rem"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title="Informações" arrangement="fullRow" border={false} padding="small">
        <>
          <DefaultFormAutocomplete
            name='idVerificacaoChecklist'
            label='Item de Inspeção'
            multiLineRows={4}
            options={inspecoesObraItens}
            getOptionKey={x => x.idVerificacaoCliente}
            getOptionLabel={x => x.descricao ?? ''}
            reactHookForm={hookForm}
          />

          <div className="flex flex-row gap-3 w-full justify-between">
            <DefaultFormSelectInput
              name='inspecao'
              label='Inspeção'
              options={inspecaoOptions}
              reactHookForm={hookForm}
            />

            <DefaultFormDatePickerInput
              name='dataDescricao'
              label='Data'
              reactHookForm={hookForm}
            />
          </div>
        </>
      </FormContainer>

      <FormContainer title="Descrição" arrangement="fullRow" border={false} padding="small">
        <>
          <DefaultFormInput
            name='descricao'
            label='Descrição'
            required={true}
            multiline={true}
            rows={5}
            reactHookForm={hookForm}
          />
        </>
      </FormContainer>

      <FormContainer title="Tratativa" arrangement="fullRow" border={false} padding="small" >
        <>
          <DefaultFormDatePickerInput
            name='dataTratativa'
            label='Data'
            reactHookForm={hookForm}
          />
          <DefaultFormInput
            name='tratativa'
            label='Tratativa'
            multiline={true}
            rows={5}
            reactHookForm={hookForm}
          />
        </>
      </FormContainer>

      <FormContainer title={"Arquivo"} arrangement="fullRow" padding="small">
        <>
          <div className="flex flex-row gap-2 w-full justify-between">
            <div className="w-[30%]">
              <Controller
                name='arquivo'
                control={hookForm.control}
                render={({ field }) => (selectedOcorrencia && selectedOcorrencia.nomeArquivo && selectedOcorrencia.nomeArquivo.length > 0) ? (
                  <div className="w-full flex flex-row justify-between items-center">
                    <span
                      className="cursor-pointer"
                      onMouseDown={() => handleDownload(1)}>
                      {selectedOcorrencia.nomeArquivo && selectedOcorrencia.nomeArquivo.length > 40
                        ? `${selectedOcorrencia.nomeArquivo.substring(0, 40)}...`
                        : selectedOcorrencia.nomeArquivo}
                    </span>
                    <IconButton onClick={() => handleDeleteFile(1)}>
                      <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                    </IconButton>
                  </div>
                ) : (
                  <PrimaryButton
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    disabled={saving}
                    startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                    {field.value ? normalizeFileName(field.value.name, 40) : "Escolher arquivo"}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e) => handleFileChange(e, field.onChange, 1)}
                      multiple={false} />
                  </PrimaryButton>
                )} />
            </div>

            <div className="w-[70%] ">
              <DefaultFormInput
                name='descricaoArquivo'
                label='Descrição do Arquivo'
                reactHookForm={hookForm}
              />
            </div>
          </div>

          <div className="flex flex-row gap-2 w-full justify-between">
            <div className="w-[30%]">
              <Controller
                name='arquivo2'
                control={hookForm.control}
                render={({ field }) => (selectedOcorrencia && selectedOcorrencia.nomeArquivo2 && selectedOcorrencia.nomeArquivo2.length > 0) ? (
                  <div className="w-full flex flex-row justify-between items-center">
                    <span
                      className="cursor-pointer"
                      onMouseDown={() => handleDownload(2)}>
                      {selectedOcorrencia.nomeArquivo2 && selectedOcorrencia.nomeArquivo2.length > 40
                        ? `${selectedOcorrencia.nomeArquivo2.substring(0, 40)}...`
                        : selectedOcorrencia.nomeArquivo2}
                    </span>
                    <IconButton onClick={() => handleDeleteFile(2)}>
                      <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                    </IconButton>
                  </div>
                ) : (
                  <PrimaryButton
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    disabled={saving}
                    startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                    {field.value ? normalizeFileName(field.value.name, 40) : "Escolher arquivo"}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e) => handleFileChange(e, field.onChange, 2)}
                      multiple={false} />
                  </PrimaryButton>
                )} />
            </div>
            <div className="w-[70%] ">
              <DefaultFormInput
                name='descricaoArquivo2'
                label='Descrição do Arquivo'
                reactHookForm={hookForm}
              />
            </div>
          </div>

        </>
      </FormContainer>
    </BaseFormControlledModal>
  );
}